/**
 * 문제 목록 컴포넌트
 * - 문제 목록 테이블
 * - 답형식별 색상 구분
 * - 페이지 필터링
 */

import React, { useState, useEffect } from 'react';
import { Table, Tag, Modal, Image, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined } from '@ant-design/icons';
import { workbookService } from '../../services/workbookService';
import type { Problem, AnswerType } from '../../types/workbook';

const { Option } = Select;

interface ProblemListProps {
  workbookName: string;
}

/**
 * 답형식 색상 매핑
 */
const getAnswerTypeColor = (type: AnswerType): string => {
  const colorMap: Record<AnswerType, string> = {
    M: 'blue',      // 객관식
    S: 'green',     // 보기선택
    B: 'orange',    // OX
    N: 'purple',    // 정수/소수
    E: 'magenta',   // 수식 (엄격)
    F: 'cyan',      // 수식 (유연)
    I: 'gold',      // 인수분해
    T: 'red',       // 서술형
    H: 'geekblue',  // 한글 손글씨
  };
  return colorMap[type] || 'default';
};

/**
 * 답형식 레이블 매핑
 */
const getAnswerTypeLabel = (type: AnswerType): string => {
  const labelMap: Record<AnswerType, string> = {
    M: '객관식',
    S: '보기선택',
    B: 'OX',
    N: '숫자',
    E: '수식(엄격)',
    F: '수식(유연)',
    I: '인수분해',
    T: '서술형',
    H: '한글손글씨',
  };
  return labelMap[type] || type;
};

/**
 * ProblemList 컴포넌트
 * 문제 목록 테이블
 */
export const ProblemList: React.FC<ProblemListProps> = ({ workbookName }) => {
  const [loading, setLoading] = useState(false);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [selectedPage, setSelectedPage] = useState<number | undefined>(undefined);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

  /**
   * 문제 목록 조회
   */
  const fetchProblems = async () => {
    setLoading(true);
    try {
      const data = await workbookService.getProblems(workbookName, selectedPage);
      setProblems(data);
    } catch (error) {
      console.error('문제 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 초기 로드 및 페이지 변경 시 재조회
   */
  useEffect(() => {
    if (workbookName) {
      fetchProblems();
    }
  }, [workbookName, selectedPage]);

  /**
   * 이미지 모달 열기
   */
  const handleViewImage = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    setImageModalVisible(true);
  };

  /**
   * 페이지 목록 추출 (중복 제거)
   */
  const getPageOptions = (): number[] => {
    const pageSet = new Set(problems.map((p) => p.page));
    const pages = Array.from(pageSet).sort((a, b) => a - b);
    return pages;
  };

  /**
   * 테이블 컬럼 정의
   */
  const columns: ColumnsType<Problem> = [
    {
      title: '페이지',
      dataIndex: 'page',
      key: 'page',
      width: 100,
      render: (page) => `${page}p`,
      sorter: (a, b) => a.page - b.page,
    },
    {
      title: '문제번호',
      dataIndex: 'problem_number',
      key: 'problem_number',
      width: 100,
      sorter: (a, b) => {
        // 문제번호가 숫자 또는 문자열일 수 있으므로 처리
        const aNum = typeof a.problem_number === 'number' ? a.problem_number : parseInt(String(a.problem_number));
        const bNum = typeof b.problem_number === 'number' ? b.problem_number : parseInt(String(b.problem_number));
        return aNum - bNum;
      },
    },
    {
      title: '답형식',
      dataIndex: 'answer_type',
      key: 'answer_type',
      width: 120,
      render: (type: AnswerType) => (
        <Tag color={getAnswerTypeColor(type)}>{getAnswerTypeLabel(type)}</Tag>
      ),
      filters: [
        { text: '객관식', value: 'M' },
        { text: '보기선택', value: 'S' },
        { text: 'OX', value: 'B' },
        { text: '숫자', value: 'N' },
        { text: '수식', value: 'E' },
        { text: '서술형', value: 'T' },
        { text: '한글손글씨', value: 'H' },
      ],
      onFilter: (value, record) => record.answer_type === value,
    },
    {
      title: '정답',
      dataIndex: 'answer',
      key: 'answer',
      width: 200,
      render: (answer, record) => {
        // T타입인 경우 이미지 미리보기 버튼
        if (record.answer_type === 'T' && record.answer_image_url) {
          return (
            <a onClick={() => handleViewImage(record.answer_image_url!)}>
              <EyeOutlined /> 이미지 보기
            </a>
          );
        }
        return answer;
      },
    },
  ];

  return (
    <div>
      {/* 페이지 필터 */}
      <div style={{ marginBottom: 16 }}>
        <Select
          placeholder="페이지 선택"
          allowClear
          style={{ width: 150 }}
          onChange={(value) => setSelectedPage(value)}
        >
          {getPageOptions().map((page) => (
            <Option key={page} value={page}>
              {page}페이지
            </Option>
          ))}
        </Select>
      </div>

      {/* 테이블 */}
      <Table
        columns={columns}
        dataSource={problems}
        rowKey={(record) => `${record.page}-${record.problem_number}`}
        loading={loading}
        pagination={{
          pageSize: 50,
          showTotal: (total) => `총 ${total}문제`,
        }}
        scroll={{ x: 700 }}
      />

      {/* 이미지 모달 */}
      <Modal
        title="정답 이미지"
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedImageUrl && (
          <Image
            src={selectedImageUrl}
            alt="정답 이미지"
            style={{ width: '100%' }}
            preview={false}
          />
        )}
      </Modal>
    </div>
  );
};
